import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid, Skeleton, TextField, Typography, IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import useAuthGlobal from "src/context/useAuthGlobal";
import useEmployeeDetail from "src/hooks/user/useEmployeeDetail";
import { enqueueSnackbar } from "notistack";
import UpFileBtn from "src/components/shared/UpFileBtn";
import useUserChangePassword from "src/hooks/user/useUserChangePassword";
import useMeUpdate from "src/hooks/user/useMeUpdate";
import { logoutExpiredSession } from "src/utils/authSession";

export default function UserProfileTable() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPreWriteNewPassword, setShowPreWriteNewPassword] = useState(false);

  const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowPreWriteNewPassword = () => setShowPreWriteNewPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const { userMe } = useAuthGlobal();
  const { data: userDetail, isLoading } = useEmployeeDetail(userMe?._id);
  const { trigger: resetPassword } = useUserChangePassword(userMe?._id);
  const { trigger: updateUser } = useMeUpdate(userMe?._id);

  const isPasswordChangeActive = (values) => [values?.currentPassword, values?.newPassword, values?.preWriteNewPassword].some((v) => Boolean(v?.length));

  const schema = yup.object().shape({
    fullName: yup.string().optional(),
    phone: yup
      .string()
      .optional()
      .test("phone", "Số điện thoại không hợp lệ", (value) => !value || /^\d{10,11}$/.test(value)),
    email: yup
      .string()
      .optional()
      .test("email", "Email không hợp lệ", (value) => !value || yup.string().email().isValidSync(value)),
    currentPassword: yup.string().optional(),
    newPassword: yup.string().test("new-password", function (value) {
      if (!isPasswordChangeActive(this.parent)) return true;
      if (!value) return this.createError({ message: "Vui lòng nhập Mật khẩu mới" });
      return true;
    }),
    preWriteNewPassword: yup.string().test("confirm-password", function (value) {
      if (!isPasswordChangeActive(this.parent)) return true;
      if (!value) return this.createError({ message: "Vui lòng nhập lại Mật khẩu mới" });
      if (value !== this.parent.newPassword) {
        return this.createError({ message: "Mật khẩu xác nhận không khớp" });
      }
      return true;
    }),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      fullName: "",
      phone: "",
      email: "",
      avatar: "",
      username: "",
      currentPassword: "",
      newPassword: "",
      preWriteNewPassword: "",
    },
  });

  const formValues = watch();
  const passwordChangeActive = isPasswordChangeActive(formValues);
  const passwordFieldsValid =
    !passwordChangeActive ||
    (Boolean(formValues?.newPassword) &&
      Boolean(formValues?.preWriteNewPassword) &&
      formValues.newPassword === formValues.preWriteNewPassword &&
      !errors.newPassword &&
      !errors.preWriteNewPassword);

  const hasProfileFieldErrors = !!(errors.fullName || errors.phone || errors.email);
  const canSaveProfile = isDirty && passwordFieldsValid && !hasProfileFieldErrors;

  useEffect(() => {
    if (userMe) {
      reset({
        fullName: userMe?.fullName ?? "",
        phone: userMe?.phone ?? "",
        email: userMe?.email ?? "",
        avatar: userMe?.avatar ?? "",
        username: userMe?.username ?? "",
        currentPassword: "",
        newPassword: "",
        preWriteNewPassword: "",
      });
    }
  }, [userMe, reset]);

  const handleEdit = async (data) => {
    try {
      const { currentPassword, newPassword, preWriteNewPassword, username, ...profileData } = data;
      const profileBody = {
        ...profileData,
        username: username,
        avatar: profileData?.avatar ? profileData.avatar : undefined,
      };
      if (!newPassword) {
        const res = await updateUser({ body: profileBody });
        enqueueSnackbar(res?.success ? "Thành công!" : res?.error?.message || "Đã có lỗi xảy ra", {
          variant: res?.success ? "success" : "error",
        });
        if (res?.success) {
          reset({
            ...profileData,
            username: username ?? userMe?.username ?? "",
            currentPassword: "",
            newPassword: "",
            preWriteNewPassword: "",
          });
        }
        return;
      }

      const res = await updateUser({ body: profileBody });
      if (res?.success) {
        await handleResetPassword({ currentPassword, preWriteNewPassword });
        reset({
          ...profileData,
          username: username ?? userMe?.username ?? "",
          currentPassword: "",
          newPassword: "",
          preWriteNewPassword: "",
        });
      } else {
        enqueueSnackbar(res?.error?.message || "Đã có lỗi xảy ra", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error.message || "Đã có lỗi xảy ra", { variant: "error" });
    }
  };

  const handleResetPassword = async (data) => {
    try {
      const res = await resetPassword({
        body: {
          currentPassword: data.currentPassword,
          password: data.preWriteNewPassword,
        },
      });
      enqueueSnackbar(res?.success ? "Thành công!" : res?.error?.message || "Đã có lỗi xảy ra", {
        variant: res?.success ? "success" : "error",
      });
      logoutExpiredSession();
    } catch (error) {
      enqueueSnackbar(error.message || "Đã có lỗi xảy ra", { variant: "error" });
    }
  };

  return (
    <Grid
      container
      gap={1.5}
      border="1px solid var(--grey)"
      borderRadius={1.5}
    >
      <Grid
        item
        xs={12}
        borderBottom="1px solid var(--grey)"
        p={1.5}
      >
        <Typography variant="subtitle1">Xem và Chỉnh sửa hồ sơ</Typography>
        <Typography color="var(--main-grey)">Chi tiết Hồ sơ</Typography>
      </Grid>
      <form
        style={{ width: "100%", padding: "12px" }}
        onSubmit={handleSubmit(handleEdit)}
      >
        <Grid
          container
          spacing={4}
        >
          <Grid
            xs={12}
            item
          >
            <Controller
              name="avatar"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <UpFileBtn
                  specific
                  sx={{ width: "200px", height: "200px" }}
                  value={field.value}
                  onChange={(file) => field.onChange(file)}
                  error={error}
                />
              )}
            />
          </Grid>
          <Grid
            xs={12}
            item
            sx={{ maxWidth: "600px !important" }}
          >
            {[
              { name: "username", label: "Tên đăng nhập" },
              { name: "fullName", label: "Họ tên" },
              { name: "phone", label: "Số điện thoại" },
              { name: "email", label: "Email" },
              {
                name: "currentPassword",
                label: "Nhập mật khẩu hiện tại",
                type: "password",
              },
              { name: "newPassword", label: "Nhập mật khẩu mới", type: "password" },
              {
                name: "preWriteNewPassword",
                label: "Nhập lại mật khẩu mới",
                type: "password",
              },
            ].map((fieldConfig, idx) => {
              let type = fieldConfig.type || "text";
              let InputProps = undefined;

              if (fieldConfig.name === "currentPassword") {
                type = showCurrentPassword ? "text" : "password";
                InputProps = {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowCurrentPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff sx={{ width: "18px" }} /> : <Visibility sx={{ width: "18px" }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                };
              } else if (fieldConfig.name === "newPassword") {
                type = showNewPassword ? "text" : "password";
                InputProps = {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff sx={{ width: "18px" }} /> : <Visibility sx={{ width: "18px" }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                };
              } else if (fieldConfig.name === "preWriteNewPassword") {
                type = showPreWriteNewPassword ? "text" : "password";
                InputProps = {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPreWriteNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPreWriteNewPassword ? <VisibilityOff sx={{ width: "18px" }} /> : <Visibility sx={{ width: "18px" }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                };
              }

              return (
                <Grid
                  item
                  xs={12}
                  mt={idx > 0 ? "24px" : 0}
                  key={fieldConfig.name}
                >
                  {!isLoading ? (
                    <Controller
                      disabled={fieldConfig?.disabled}
                      name={fieldConfig.name}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type={type}
                          size="small"
                          label={fieldConfig.label}
                          fullWidth
                          error={!!errors[fieldConfig.name]}
                          helperText={errors[fieldConfig.name]?.message}
                          InputProps={InputProps}
                        />
                      )}
                    />
                  ) : (
                    <Skeleton
                      width="100%"
                      height="56px"
                    />
                  )}
                </Grid>
              );
            })}
            <Grid
              item
              xs={12}
              mt="24px"
            >
              {!isLoading ? (
                <Button
                  disabled={!canSaveProfile}
                  type="submit"
                  fullWidth
                  variant="contained"
                >
                  Lưu hồ sơ
                </Button>
              ) : (
                <Skeleton
                  width="100%"
                  height="56px"
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
}
